import { useRef, useState } from 'react';
import { BREAKPOINTS_WIDTHS, useWindowSize } from '@belong/common';
import { Text } from '@belong/ui';
import clsx from 'classnames';
import BDate, { DATE_TYPES } from 'components/BDate/BDate';
import { GoogleMapMobile } from 'components/GoogleMap/HomesMap/GoogleMapMobile/GoogleMapMobile';
import googleMapReact from 'google-map-react';
import { formatString } from 'strings';
import { LastUpdated } from 'strings/common.strings';
import type Supercluster from 'supercluster';
import useSupercluster from 'use-supercluster';
import { ComparablesMap } from './comparables-map';
import type { ComparableHomeProperties } from './comparables-map';
import styles from './comparables-step-map.module.css';

type ComparablesStepMapProps = {
  homes?: any[];
  lastUpdatedOn?: Date;
};

export function ComparablesStepMap({
  homes,
  lastUpdatedOn,
}: ComparablesStepMapProps) {
  const [active, setActive] = useState<number | null>(null);
  const [hover, setShowHover] = useState<number | null>(null);
  const [modal, setModal] = useState(false);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);
  const { width } = useWindowSize();
  const mapRef = useRef<any>();
  const [bounds, setBounds] = useState<number[]>([]);
  const [zoom, setZoom] = useState<number>(8);

  // `homes` is optional, and `.map` on it would already have thrown — the guard
  // only replaces a TypeError, it does not skip work that used to happen.
  const points: Supercluster.PointFeature<ComparableHomeProperties>[] = (
    homes ?? []
  ).map((home, index) => ({
    type: 'Feature',
    properties: { cluster: false, category: 'homes', homeId: index, home },
    geometry: {
      type: 'Point',
      coordinates: [
        parseFloat(home?.address?.coordinates?.longitude || '0'),
        parseFloat(home?.address?.coordinates?.latitude || '0'),
      ],
    },
  }));
  const { clusters, supercluster } = useSupercluster({
    points,
    // `bounds` starts as `[]`, which is not a valid `BBox` — but it is truthy,
    // so `use-supercluster` does call `getClusters([], zoom)` on the first
    // render, before `onChange` supplies real bounds. Cast rather than
    // "corrected" to `undefined`, so this keeps its existing behaviour; the
    // latent issue is recorded in the typecheck-baseline follow-ups doc.
    bounds: bounds as GeoJSON.BBox,
    zoom,
    options: { radius: 75, maxZoom: 20 },
  });

  const isMobile = width <= BREAKPOINTS_WIDTHS.MD;

  const distanceToMouse = (
    { x, y }: googleMapReact.Point,
    { x: mouseX, y: mouseY }: googleMapReact.Point,
  ) => {
    return Math.max(Math.abs(mouseX - x), Math.abs(mouseY - y + 32));
  };

  // `google-map-react` types the child hover key as `any` — it is whatever the
  // child's `key` was, which here is a numeric `homeId` for home markers but a
  // `cluster-<id>` string for clusters. Matching the library rather than
  // narrowing to `number`, which would be wrong for the cluster case.
  const onChildClick = (childIndex: any) => {
    if (active !== childIndex) {
      setActive(childIndex);
    } else {
      setActive(null);
    }
  };

  const onMainMapChildClick = (childIndex: any) => {
    if (!isMobile) {
      if (active !== childIndex) {
        setActive(childIndex);
      } else {
        setActive(null);
      }
    } else {
      setModal(true);
      setHasBeenOpened(true);
    }
  };

  const setAndRemoveCorrectZoom = (currentBounds: google.maps.LatLngBounds) => {
    mapRef.current.setOptions({ maxZoom: 17 });

    google.maps.event.addListenerOnce(mapRef.current, 'idle', () => {
      mapRef.current.setOptions({ maxZoom: null });
    });

    mapRef.current.fitBounds(currentBounds);
  };

  const handleApiLoaded = (map: google.maps.Map) => {
    mapRef.current = map;
    if (isMobile) {
      mapRef.current.setOptions({ draggableCursor: 'pointer' });
    }

    if (homes?.length) {
      const bounds = new google.maps.LatLngBounds();

      homes.forEach((home) => {
        bounds.extend(
          new google.maps.LatLng(
            parseFloat(home.address.coordinates?.latitude || 0),
            parseFloat(home.address.coordinates?.longitude || 0),
          ),
        );
      });
      setAndRemoveCorrectZoom(bounds);
    }
  };

  function onChange(event: googleMapReact.ChangeEventValue) {
    setZoom(event.zoom);
    setBounds([
      event.bounds.nw.lng,
      event.bounds.se.lat,
      event.bounds.se.lng,
      event.bounds.nw.lat,
    ]);
  }

  function onClusterClick({ cluster, latitude, longitude }: any) {
    return () => {
      // `supercluster` is undefined until the hook has loaded the points. A
      // click can only come from a rendered cluster, which cannot exist before
      // then, so this guard is unreachable in practice.
      if (!supercluster) return;

      const expansionZoom = Math.min(
        supercluster.getClusterExpansionZoom(cluster.id),
        20,
      );
      mapRef.current.setZoom(expansionZoom);
      mapRef.current.panTo({ lat: latitude, lng: longitude });
    };
  }

  return (
    <>
      <div
        className={clsx(
          'rounded border border-solid border-gray md:relative md:w-full',
          styles.comparablesStepMapContainer,
        )}
      >
        {isMobile && hasBeenOpened && (
          <GoogleMapMobile
            onHide={() => {
              setModal(false);
              setActive(null);
              setShowHover(null);
            }}
            visible={modal}
          >
            <ComparablesMap
              hasPointer={isMobile}
              clusters={clusters}
              points={points}
              onClusterClick={onClusterClick}
              hover={hover}
              active={active}
              distanceToMouse={distanceToMouse}
              onChildMouseEnter={(childIndex) => {
                setShowHover(childIndex);
              }}
              onChildMouseLeave={() => {
                setShowHover(null);
              }}
              onChildClick={onChildClick}
              onClick={() => {
                if (isMobile) {
                  setModal(true);
                  setHasBeenOpened(true);
                }
                setActive(null);
              }}
              onChange={onChange}
              setActive={setActive}
              homes={homes}
              zoomControlPosition="RIGHT_TOP"
              handleApiLoaded={handleApiLoaded}
              hoverDistance={36}
            />
          </GoogleMapMobile>
        )}

        <ComparablesMap
          hasPointer={isMobile}
          clusters={clusters}
          points={points}
          onClusterClick={onClusterClick}
          hover={hover}
          active={active}
          gesture={isMobile ? 'cooperative' : 'greedy'}
          distanceToMouse={distanceToMouse}
          onChildMouseEnter={(childIndex) => {
            if (!isMobile) {
              setShowHover(childIndex);
            }
          }}
          onChildMouseLeave={() => {
            setShowHover(null);
          }}
          onChildClick={onMainMapChildClick}
          onClick={() => {
            if (isMobile) {
              setModal(true);
              setHasBeenOpened(true);
            } else {
              setActive(null);
            }
          }}
          onChange={onChange}
          setActive={setActive}
          zoomControlPosition="RIGHT_BOTTOM"
          homes={homes}
          handleApiLoaded={handleApiLoaded}
          hoverDistance={36}
        />
      </div>
      <div className="mt-xs flex justify-end">
        <Text fontWeight="semibold" className="text-dark-gray" variant="p1">
          {formatString(LastUpdated.last_updated_general, {
            date: (
              <BDate
                time={lastUpdatedOn}
                formatType={DATE_TYPES.DOTTED_MEDIUM}
              />
            ),
          })}
        </Text>
      </div>
    </>
  );
}
